/**
 * syntaxValidator.js
 * Comprehensive syntax recognition, delimiter validation, and condition analysis engine
 * for NoCode Legal Document Automation Platform.
 */

/**
 * Analyzes a raw condition string inside [ ... ]
 * e.g. "<<Status>> == Overdue", "<<Loan Amount>> > 50000", "<<City>> == Mumbai || <<City>> == Pune"
 */
export function analyzeConditionText(condText) {
  const issues = [];
  const clean = (condText || '').trim();

  if (!clean) {
    return {
      isValid: false,
      summary: 'Empty condition',
      issues: [{ from: 0, to: 1, type: 'empty_condition', severity: 'error', message: 'Condition is empty: specify variable and comparison rule (e.g. [<<Status>> == "Active"])' }]
    };
  }

  // 1. Check for single '=' comparison (common mistake: [<<Var>> = Value])
  const singleEqMatch = /(?<![!=<>])=(?![=><])/g;
  let eqM;
  while ((eqM = singleEqMatch.exec(condText)) !== null) {
    issues.push({
      from: eqM.index,
      to: eqM.index + 1,
      type: 'single_equals',
      severity: 'error',
      message: "Condition error: Use '==' for equality comparison, not single '='"
    });
  }

  // 2. Check for invalid double operators like '>>' or '<<' used as operators
  const invalidOpMatch = /(?:>>|<<)\s*(?:>>|<<)/g;
  let opM;
  while ((opM = invalidOpMatch.exec(condText)) !== null) {
    issues.push({
      from: opM.index,
      to: opM.index + opM[0].length,
      type: 'invalid_operator',
      severity: 'error',
      message: "Condition error: Invalid operator syntax"
    });
  }

  // 3. Check for missing value after comparison operators at end of condition or before logical combiners
  const trailingOpRegex = /(?:===|==|!==|!=|>=|<=|(?<![>])>(?![>])|(?<![<])<(?![<]))\s*(?=(?:AND|OR|&&|\|\|)|$)/gi;
  let trM;
  while ((trM = trailingOpRegex.exec(condText)) !== null) {
    const op = trM[0].trim();
    issues.push({
      from: trM.index,
      to: trM.index + trM[0].length,
      type: 'missing_value',
      severity: 'error',
      message: `Condition incomplete: missing comparison value after '${op}'`
    });
  }

  // 4. Check for missing variable before comparison operators
  const leadingOpRegex = /(?:^|\s+(?:AND|OR|&&|\|\|)\s*)\s*(===|==|!==|!=|>=|<=|(?<![>])>(?![>])|(?<![<])<(?![<]))/gi;
  let leadM;
  while ((leadM = leadingOpRegex.exec(condText)) !== null) {
    issues.push({
      from: leadM.index,
      to: leadM.index + leadM[0].length,
      type: 'missing_variable',
      severity: 'error',
      message: `Condition incomplete: missing variable before '${leadM[1]}'`
    });
  }

  // 5. Check for bare variable (not in << >>) before operators: e.g. [Status == Overdue]
  const bareVarRegex = /(?:^|\s+(?:AND|OR|&&|\|\|)\s+)\s*([a-zA-Z_][a-zA-Z0-9_\s]*?)\s*(?:===|==|!==|!=|>=|<=|(?<![>])>(?![>])|(?<![<])<(?![<]))/gi;
  let bvm;
  while ((bvm = bareVarRegex.exec(condText)) !== null) {
    const raw = bvm[1].trim();
    if (raw && !raw.includes('<<') && !raw.includes('>>') && !/^(AND|OR|true|false)$/i.test(raw)) {
      const idx = condText.indexOf(raw, bvm.index);
      issues.push({
        from: idx,
        to: idx + raw.length,
        type: 'bare_variable_warning',
        severity: 'warning',
        message: `Condition hint: Variable '${raw}' should be enclosed in '<<...>>' (e.g. <<${raw}>>)`
      });
    }
  }

  // Build human-readable condition summary
  let summary = '';
  if (issues.length === 0) {
    const cleanTokens = clean
      .replace(/<<([^>]+)>>/g, '$1')
      .replace(/==/g, 'equals')
      .replace(/!=/g, 'does not equal')
      .replace(/>=/g, '>=')
      .replace(/<=/g, '<=');
    summary = `Condition: ${cleanTokens}`;
  }

  return {
    isValid: issues.filter(i => i.severity === 'error').length === 0,
    summary,
    issues
  };
}

/**
 * Scans a single text string for all syntax errors, including:
 * - Stray / extra delimiters (<, >, {, }, [, ])
 * - Incomplete or malformed variables (<<Var without >>, <<Var>, <Var>>, <<>>)
 * - Incomplete commands (<page..., <lin...)
 * - Unrecognized commands (<unfinished>)
 * - Unmatched braces and brackets
 * - Condition errors inside [...]
 */
export function scanTextForSyntaxErrors(text, isStartOfBlock = false) {
  if (!text || typeof text !== 'string') return [];
  const errors = [];

  // Helper to test if a sub-range already overlaps an existing error
  const hasOverlap = (from, to) => {
    return errors.some(e => !(to <= e.from || from >= e.to));
  };

  // ── 1. Malformed Variable Tags ──────────────────────────────────────────────
  // 1a. <Variable>> (single '<' with double '>>')
  const singleOpenVar = /(?<!<)<([^<>«»\n]+)>>(?!>)/g;
  let sovM;
  while ((sovM = singleOpenVar.exec(text)) !== null) {
    errors.push({
      from: sovM.index,
      to: sovM.index + sovM[0].length,
      type: 'malformed_variable_single_open',
      severity: 'error',
      text: sovM[0],
      message: `Malformed variable syntax: single '<' found, expected '<<${sovM[1]}>>'`
    });
  }

  // 1b. <<Variable> (double '<<' with single '>')
  const singleCloseVar = /(?<!<)<<([^<>«»\n]+)>(?!>)/g;
  let scvM;
  while ((scvM = singleCloseVar.exec(text)) !== null) {
    errors.push({
      from: scvM.index,
      to: scvM.index + scvM[0].length,
      type: 'malformed_variable_single_close',
      severity: 'error',
      text: scvM[0],
      message: `Malformed variable syntax: single '>' found, expected '<<${scvM[1]}>>'`
    });
  }

  // 1c. <<Variable without closing >>
  const unclosedVar = /(?<!<)<<([^<>«»\n]+)(?![^«»\n]*>>)/g;
  let uvM;
  while ((uvM = unclosedVar.exec(text)) !== null) {
    if (!hasOverlap(uvM.index, uvM.index + uvM[0].length)) {
      errors.push({
        from: uvM.index,
        to: uvM.index + uvM[0].length,
        type: 'unclosed_variable',
        severity: 'error',
        text: uvM[0],
        message: `Incomplete variable syntax: missing closing '>>'`
      });
    }
  }

  // 1d. Empty variable placeholder: <<>> or <<   >>
  const emptyVar = /<<\s*>>/g;
  let evM;
  while ((evM = emptyVar.exec(text)) !== null) {
    errors.push({
      from: evM.index,
      to: evM.index + evM[0].length,
      type: 'empty_variable',
      severity: 'error',
      text: evM[0],
      message: `Malformed variable: empty variable placeholder`
    });
  }

  // ── 2. Incomplete or Unrecognized Commands ──────────────────────────────────
  // 2a. Incomplete command prefixes: <page..., <lin...
  const incCmd = /(?<!<)<(page(?:\s+break|\s+bre|\s+b|\s+)?|lin(?:e)?)(?![^<>\n]*>)/gi;
  let icM;
  while ((icM = incCmd.exec(text)) !== null) {
    if (!hasOverlap(icM.index, icM.index + icM[0].length)) {
      const isPb = icM[0].toLowerCase().startsWith('<page');
      errors.push({
        from: icM.index,
        to: icM.index + icM[0].length,
        type: 'incomplete_command',
        severity: 'error',
        text: icM[0],
        message: `Incomplete ${isPb ? 'page break' : 'line'} command: expected '${isPb ? '<page break>' : '<line>'}'`
      });
    }
  }

  // 2b. Unrecognized single-bracket command: <something> that is not page break / line
  const singleAngleCmd = /(?<!<)<([^<>«»\n]+)>(?!>)/g;
  let sacM;
  while ((sacM = singleAngleCmd.exec(text)) !== null) {
    const inner = sacM[1].trim().toLowerCase();
    if (inner === 'page break' || inner === 'line' || inner === 'line break') continue;
    if (!hasOverlap(sacM.index, sacM.index + sacM[0].length)) {
      errors.push({
        from: sacM.index,
        to: sacM.index + sacM[0].length,
        type: 'unrecognized_command',
        severity: 'warning',
        text: sacM[0],
        message: `Unrecognized command '${sacM[0]}'. Supported commands are '<page break>' and '<line>'.`
      });
    }
  }

  // ── 3. Extra / Stray Angle Brackets (< and >) ──────────────────────────────
  // 3a. Stray closing '>' that is not part of valid command, variable, arrow (=>, ->), or condition
  const strayGtRegex = /(?<![<>\-=])>(?![>=\-])/g;
  let sgtM;
  while ((sgtM = strayGtRegex.exec(text)) !== null) {
    const idx = sgtM.index;
    if (hasOverlap(idx, idx + 1)) continue;

    // Check if it is inside [...] condition
    const before = text.substring(0, idx);
    const inBracket = before.lastIndexOf('[') > before.lastIndexOf(']');
    // Check if it is valid command <page break> or <line>
    const lastLt = before.lastIndexOf('<');
    const isValidCommand = lastLt !== -1 && !before.substring(lastLt).includes('>') &&
      (/^<page\s+break>$/i.test(text.substring(lastLt, idx + 1)) || /^<line>$/i.test(text.substring(lastLt, idx + 1)));

    // Check if it is alignment prefix at block start
    const isBlockStartAlign = isStartOfBlock && (idx === 0 || /^[\s]*$/.test(text.substring(0, idx)));

    if (!inBracket && !isValidCommand && !isBlockStartAlign) {
      errors.push({
        from: idx,
        to: idx + 1,
        type: 'stray_closing_delimiter',
        severity: 'error',
        text: '>',
        message: "Extra / stray closing delimiter '>'"
      });
    }
  }

  // 3b. Extra closing delimiters: '>>>' (three or more >)
  const tripleGtRegex = />>>+/g;
  let tgtM;
  while ((tgtM = tripleGtRegex.exec(text)) !== null) {
    const startIdx = tgtM.index + 2;
    const endIdx = tgtM.index + tgtM[0].length;
    if (!hasOverlap(startIdx, endIdx)) {
      errors.push({
        from: startIdx,
        to: endIdx,
        type: 'extra_closing_delimiters',
        severity: 'error',
        text: text.substring(startIdx, endIdx),
        message: `Extra closing delimiter '>' (${endIdx - startIdx} extra)`
      });
    }
  }

  // 3c. Extra opening delimiters: '<<<' (three or more <)
  const tripleLtRegex = /<<<+/g;
  let tltM;
  while ((tltM = tripleLtRegex.exec(text)) !== null) {
    const startIdx = tltM.index;
    const endIdx = tltM.index + tltM[0].length - 2;
    if (!hasOverlap(startIdx, endIdx)) {
      errors.push({
        from: startIdx,
        to: endIdx,
        type: 'extra_opening_delimiters',
        severity: 'error',
        text: text.substring(startIdx, endIdx),
        message: `Extra opening delimiter '<' (${endIdx - startIdx} extra)`
      });
    }
  }

  // 3d. Standalone opening '<' that does not form << or <command> or comparison <= or alignment <=
  const standaloneLtRegex = /(?<![<])<(?![<=]|\s*\/|(?:page\s+break|line)\b)/gi;
  let sltM;
  while ((sltM = standaloneLtRegex.exec(text)) !== null) {
    const idx = sltM.index;
    if (hasOverlap(idx, idx + 1)) continue;

    const before = text.substring(0, idx);
    const inBracket = before.lastIndexOf('[') > before.lastIndexOf(']');
    const isBlockStartAlign = isStartOfBlock && (idx === 0 || /^[\s]*$/.test(text.substring(0, idx)));

    if (!inBracket && !isBlockStartAlign) {
      errors.push({
        from: idx,
        to: idx + 1,
        type: 'stray_opening_delimiter',
        severity: 'error',
        text: '<',
        message: "Extra / stray opening delimiter '<'"
      });
    }
  }

  // ── 4. Unmatched and Extra Braces ({ and }) ──────────────────────────────────
  // Check for duplicate braces: '{{' or '}}'
  const dupOpenBrace = /\{\{+/g;
  let dobM;
  while ((dobM = dupOpenBrace.exec(text)) !== null) {
    const start = dobM.index + 1;
    const end = dobM.index + dobM[0].length;
    if (!hasOverlap(start, end)) {
      errors.push({
        from: start,
        to: end,
        type: 'extra_opening_brace',
        severity: 'error',
        text: text.substring(start, end),
        message: `Extra opening brace '{'`
      });
    }
  }

  const dupCloseBrace = /\}\}+/g;
  let dcbM;
  while ((dcbM = dupCloseBrace.exec(text)) !== null) {
    const start = dcbM.index + 1;
    const end = dcbM.index + dcbM[0].length;
    if (!hasOverlap(start, end)) {
      errors.push({
        from: start,
        to: end,
        type: 'extra_closing_brace',
        severity: 'error',
        text: text.substring(start, end),
        message: `Extra closing brace '}'`
      });
    }
  }

  // Balance check for braces { and }
  let bDepth = 0;
  let lastOpenBraceIdx = -1;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '{') {
      if (bDepth > 0 && lastOpenBraceIdx !== -1 && !hasOverlap(lastOpenBraceIdx, lastOpenBraceIdx + 1)) {
        errors.push({
          from: lastOpenBraceIdx,
          to: lastOpenBraceIdx + 1,
          type: 'unmatched_brace',
          severity: 'error',
          text: '{',
          message: "Unmatched opening brace '{'"
        });
      }
      bDepth++;
      lastOpenBraceIdx = i;
    } else if (text[i] === '}') {
      if (bDepth === 0) {
        if (!hasOverlap(i, i + 1)) {
          errors.push({
            from: i,
            to: i + 1,
            type: 'stray_closing_brace',
            severity: 'error',
            text: '}',
            message: "Extra / stray closing brace '}'"
          });
        }
      } else {
        bDepth--;
        lastOpenBraceIdx = -1;
      }
    }
  }
  if (bDepth > 0 && lastOpenBraceIdx !== -1 && !hasOverlap(lastOpenBraceIdx, lastOpenBraceIdx + 1)) {
    errors.push({
      from: lastOpenBraceIdx,
      to: lastOpenBraceIdx + 1,
      type: 'unclosed_brace',
      severity: 'error',
      text: '{',
      message: "Unmatched opening brace '{': missing closing '}'"
    });
  }

  // ── 5. Unmatched and Extra Brackets ([ and ]) & Condition Validation ─────────
  // Check for duplicate brackets: '[[' or ']]'
  const dupOpenBracket = /\[\[+/g;
  let dobkM;
  while ((dobkM = dupOpenBracket.exec(text)) !== null) {
    const start = dobkM.index + 1;
    const end = dobkM.index + dobkM[0].length;
    if (!hasOverlap(start, end)) {
      errors.push({
        from: start,
        to: end,
        type: 'extra_opening_bracket',
        severity: 'error',
        text: text.substring(start, end),
        message: `Extra opening bracket '['`
      });
    }
  }

  const dupCloseBracket = /\]\]+/g;
  let dcbkM;
  while ((dcbkM = dupCloseBracket.exec(text)) !== null) {
    const start = dcbkM.index + 1;
    const end = dcbkM.index + dcbkM[0].length;
    if (!hasOverlap(start, end)) {
      errors.push({
        from: start,
        to: end,
        type: 'extra_closing_bracket',
        severity: 'error',
        text: text.substring(start, end),
        message: `Extra closing bracket ']'`
      });
    }
  }

  // Bracket pairing and condition analysis
  let bkDepth = 0;
  let lastOpenBkIdx = -1;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '[') {
      if (bkDepth > 0 && lastOpenBkIdx !== -1 && !hasOverlap(lastOpenBkIdx, lastOpenBkIdx + 1)) {
        errors.push({
          from: lastOpenBkIdx,
          to: lastOpenBkIdx + 1,
          type: 'unmatched_bracket',
          severity: 'error',
          text: '[',
          message: "Unmatched opening bracket '['"
        });
      }
      bkDepth++;
      lastOpenBkIdx = i;
    } else if (text[i] === ']') {
      if (bkDepth === 0) {
        if (!hasOverlap(i, i + 1)) {
          errors.push({
            from: i,
            to: i + 1,
            type: 'stray_closing_bracket',
            severity: 'error',
            text: ']',
            message: "Extra / stray closing bracket ']'"
          });
        }
      } else {
        // Paired bracket found: analyze the condition text inside [ ... ]
        const innerCond = text.substring(lastOpenBkIdx + 1, i);
        const condAnalysis = analyzeConditionText(innerCond);
        if (condAnalysis.issues.length > 0) {
          condAnalysis.issues.forEach(iss => {
            const errFrom = lastOpenBkIdx + 1 + iss.from;
            const errTo = lastOpenBkIdx + 1 + iss.to;
            if (!hasOverlap(errFrom, errTo)) {
              errors.push({
                from: errFrom,
                to: errTo,
                type: iss.type,
                severity: iss.severity,
                text: text.substring(errFrom, errTo),
                message: iss.message
              });
            }
          });
        }
        bkDepth--;
        lastOpenBkIdx = -1;
      }
    }
  }
  if (bkDepth > 0 && lastOpenBkIdx !== -1 && !hasOverlap(lastOpenBkIdx, lastOpenBkIdx + 1)) {
    errors.push({
      from: lastOpenBkIdx,
      to: lastOpenBkIdx + 1,
      type: 'unclosed_bracket',
      severity: 'error',
      text: '[',
      message: "Unmatched opening bracket '[': missing closing ']'"
    });
  }

  // Sort errors by document order
  return errors.sort((a, b) => a.from - b.from);
}

export default {
  analyzeConditionText,
  scanTextForSyntaxErrors
};

